import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import ReceiptViewerModal from '../components/ReceiptViewerModal';
import { useUnifiedSync } from '../hooks/useUnifiedSync';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';
import { useSearchParams } from 'react-router-dom';

interface RevolutExpense {
  id: number;
  revolut_id: string;
  transaction_id: string | null;
  state: string;
  transaction_type: string | null;
  description: string | null;
  payer: string | null;
  merchant: string | null;
  expense_date: string;
  submitted_at: string | null;
  completed_at: string | null;
  amount: {
    value: number | null;
    currency: string | null;
  };
  spent_amount: {
    value: number | null;
    currency: string | null;
  };
  category: {
    name: string | null;
    code: string | null;
  };
  tax_rate: {
    name: string | null;
    percentage: number | null;
  };
  labels: Record<string, any>;
  splits: any[];
  receipt_ids: string[];
  account_source: string;
  created_at: string;
  updated_at: string;
}

interface ExpenseResponse {
  expenses: RevolutExpense[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

const RevolutExpenses: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [expenseData, setExpenseData] = useState<ExpenseResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [hasReceiptFilter, setHasReceiptFilter] = useState('');
  const [selectedExpense, setSelectedExpense] = useState<RevolutExpense | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState<string>('');
  const [selectedExpenseForReceipt, setSelectedExpenseForReceipt] = useState<RevolutExpense | null>(null);
  const [fetchingReceipts, setFetchingReceipts] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('expense_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const focusExpenseId = (searchParams.get('focus_expense_id') || '').trim();
  
  // Progress modal state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  
  // Unified sync hook - handles both historical and incremental sync
  const {
    syncStatus,
    syncProgress,
    isSyncing,
    syncType,
    startSync,
    stopSync,
    error: syncError
  } = useUnifiedSync({
    dataType: 'expenses',
    account: 'ireland', // Expenses are currently only for Ireland account
    autoSyncOnMount: true,
    autoSyncDelay: 500
  });
  
  // Show progress modal automatically when sync starts
  useEffect(() => {
    if (isSyncing && syncProgress) {
      setProgressModalOpen(true);
    } else if (syncStatus === 'completed' || syncStatus === 'failed') {
      // Keep modal open briefly to show completion/failure, then auto-close after a delay
      setTimeout(() => {
        setProgressModalOpen(false);
      }, 3000);
    }
  }, [isSyncing, syncProgress, syncStatus]);
  
  // Refresh data when sync completes
  useEffect(() => {
    if (syncStatus === 'completed') {
      setTimeout(() => {
        fetchExpenseData();
      }, 1500);
    }
  }, [syncStatus]);
  
  // Delete all expenses
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const fetchExpenseData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (stateFilter) {
        params.append('state', stateFilter);
      }
      
      if (typeFilter) {
        params.append('transaction_type', typeFilter);
      }
      
      if (hasReceiptFilter) {
        params.append('has_receipt', hasReceiptFilter);
      }
      if (focusExpenseId) {
        params.append('revolut_id', focusExpenseId);
      }

      const response = await api.get(`/api/revolut/expenses?${params}`);
      setExpenseData(response.data);
    } catch (err: any) {
      console.error('Error fetching expense data:', err);
      setError(err.response?.data?.error || 'Failed to fetch expense data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-sync is now handled by useUnifiedSync hook - no need for manual sync event listener
  useEffect(() => {
    // Fetch existing data on mount
    fetchExpenseData();
  }, []); // Only run on mount

  // Old progress polling functions removed - now using unified sync hook which handles progress polling

  // Handle delete all expenses
  const handleDeleteAllExpenses = async () => {
    try {
      setDeleting(true);
      setError(null);
      
      const response = await api.post('/api/revolut/expenses/clear');
      
      if (response.data.success) {
        const deletedCount = response.data.deleted_count || 0;
        const deletedReceiptsCount = response.data.deleted_receipts_count || 0;
        const receiptIdsFound = response.data.receipt_ids_found || 0;
        
        let message = `✅ Successfully deleted ${deletedCount} expenses`;
        if (deletedReceiptsCount > 0) {
          message += ` and ${deletedReceiptsCount} receipt file${deletedReceiptsCount !== 1 ? 's' : ''}`;
        }
        if (receiptIdsFound > 0 && deletedReceiptsCount === 0) {
          message += `\n\n⚠️ Note: Found ${receiptIdsFound} receipt ID${receiptIdsFound !== 1 ? 's' : ''} in the database, but no receipt files were found on disk to delete. This is normal if receipts were never downloaded or files were already deleted.`;
        }
        message += '\n\nAll expense records and receipt IDs have been cleared from the database.';
        
        alert(message);
        setDeleteDialogOpen(false);
        // Refresh the expense list
        await fetchExpenseData();
        // Refresh sync status to show "Not synced yet" after deletion
        window.dispatchEvent(new CustomEvent('revolut-sync-status-refresh'));
        // Also refresh after a delay to ensure backend has updated
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('revolut-sync-status-refresh'));
        }, 1000);
      } else {
        setError(response.data.error || 'Failed to delete expenses');
      }
    } catch (err: any) {
      console.error('Error deleting expenses:', err);
      setError(err.response?.data?.error || 'Failed to delete expenses');
    } finally {
      setDeleting(false);
    }
  };

  // Unified sync handler - replaces old syncExpenses function
  const handleUnifiedSync = async () => {
    await startSync();
  };

  const fetchReceipt = async (expenseId: string, receiptId: string) => {
    // Find the expense object to pass to the modal
    const expense = expenseData?.expenses?.find((e: RevolutExpense) => e.revolut_id === expenseId);
    if (expense) {
      setSelectedExpenseForReceipt(expense);
      setSelectedReceiptId(receiptId);
      setReceiptDialogOpen(true);
    }
  };

  const fetchAllReceipts = async () => {
    setFetchingReceipts(true);
    try {
      // Don't send max_expenses - fetch receipts for 100% of expenses
      const response = await api.post('/api/revolut/receipts/fetch-all', {});
      
      if (response.data.success) {
        const results = response.data.results;
        const errorMsg = results.errors && results.errors.length > 0 
          ? `\n\nErrors: ${results.errors.join(', ')}`
          : '';
        alert(`Receipt fetching completed!\n\n` +
              `Total expenses checked: ${results.total_expenses_checked}\n` +
              `Expenses with receipts: ${results.expenses_with_receipts}\n` +
              `Receipts fetched: ${results.receipts_fetched}\n` +
              `Receipts already exist: ${results.receipts_already_exist}\n` +
              `Receipts failed: ${results.receipts_failed}${errorMsg}`);
        
      } else {
        alert('Failed to fetch receipts');
      }
    } catch (err: any) {
      console.error('Error fetching all receipts:', err);
      alert('Error fetching receipts: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setFetchingReceipts(false);
    }
  };


  useEffect(() => {
    fetchExpenseData();
  }, [page, stateFilter, typeFilter, hasReceiptFilter, sortBy, sortOrder, focusExpenseId]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: { value: number | null; currency: string | null } | null) => {
    if (!amount || amount.value === null || !amount.currency) return 'N/A';
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: amount.currency
    }).format(amount.value);
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'approved':
        return 'success';
      case 'pending_reimbursement':
        return 'primary';
      case 'awaiting_review':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'missing_info':
        return 'default';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'card_payment':
        return 'primary';
      case 'atm':
        return 'secondary';
      case 'fee':
        return 'warning';
      case 'transfer':
        return 'info';
      case 'external':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleViewDetails = (expense: RevolutExpense) => {
    setSelectedExpense(expense);
    setDetailDialogOpen(true);
  };

  if (loading && !expenseData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading expenses...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          {/* Header with inline filters */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            {/* Left side - Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" component="h2">
                <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Revolut Expenses
              </Typography>
            </Box>

            {/* Right side - Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Filter Toggle Button */}
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterIcon />}
                size="small"
                color="primary"
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={fetchAllReceipts}
                disabled={fetchingReceipts}
                color="secondary"
              >
                {fetchingReceipts ? 'Fetching...' : 'Fetch All Receipts'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={handleUnifiedSync}
                disabled={isSyncing || loading}
                color="primary"
              >
                {isSyncing ? 'Syncing...' : 'Sync Expenses'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleting || loading}
                color="error"
              >
                Delete All Expenses
              </Button>
              
              <Button
                variant="outlined"
                onClick={fetchExpenseData}
                startIcon={<RefreshIcon />}
                disabled={loading}
                color="primary"
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Collapsible Filters */}
          {showFilters && (
            <Box sx={{ mb: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Filters
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>State</InputLabel>
                      <Select
                        value={stateFilter}
                        label="State"
                        onChange={(e) => setStateFilter(e.target.value)}
                      >
                        <MenuItem value="">All States</MenuItem>
                        <MenuItem value="missing_info">Missing Info</MenuItem>
                        <MenuItem value="awaiting_review">Awaiting Review</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="pending_reimbursement">Pending Reimbursement</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Transaction Type</InputLabel>
                      <Select
                        value={typeFilter}
                        label="Transaction Type"
                        onChange={(e) => setTypeFilter(e.target.value)}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="card_payment">Card Payment</MenuItem>
                        <MenuItem value="atm">ATM</MenuItem>
                        <MenuItem value="fee">Fee</MenuItem>
                        <MenuItem value="transfer">Transfer</MenuItem>
                        <MenuItem value="external">External</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Has Receipt</InputLabel>
                      <Select
                        value={hasReceiptFilter}
                        label="Has Receipt"
                        onChange={(e) => setHasReceiptFilter(e.target.value)}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}


      {/* Expenses Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Expenses ({expenseData?.pagination.total_count || 0} total)
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
            <Table size="small" sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={[
                  { key: 'date', pct: 11 },
                  { key: 'state', pct: 11 },
                  { key: 'type', pct: 11 },
                  { key: 'merchant', pct: 17 },
                  { key: 'description' },
                  { key: 'amount', pct: 12 },
                  { key: 'receipts', pct: 10 },
                  { key: 'actions', pct: 8 },
                ]}
              />
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('expense_date')}
                  >
                    <strong>Date {getSortIcon('expense_date')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('state')}
                  >
                    <strong>State {getSortIcon('state')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('transaction_type')}
                  >
                    <strong>Type {getSortIcon('transaction_type')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('merchant')}
                  >
                    <strong>Merchant/Payer {getSortIcon('merchant')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('description')}
                  >
                    <strong>Description {getSortIcon('description')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('amount')}
                  >
                    <strong>Amount {getSortIcon('amount')}</strong>
                  </TableCell>
                  <TableCell><strong>Receipts</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenseData?.expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx}>
                        {formatDate(expense.expense_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={expense.state.replace('_', ' ')} 
                        color={getStateColor(expense.state) as any}
                        size="small"
                        sx={fitChipSx}
                      />
                    </TableCell>
                    <TableCell>
                      {expense.transaction_type && (
                        <Chip 
                          label={expense.transaction_type.replace('_', ' ')} 
                          color={getTransactionTypeColor(expense.transaction_type) as any}
                          size="small"
                          sx={fitChipSx}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx} title={expense.merchant || expense.payer || ''}>
                        {expense.merchant || expense.payer || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx} title={expense.description || ''}>
                        {expense.description || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        ...fitTruncateSx,
                        color: expense.spent_amount.value && expense.spent_amount.value < 0 ? 'error.main' : 'success.main' 
                      }}>
                        {formatAmount(expense.spent_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell className="fit-table-no-clip">
                      {expense.receipt_ids && expense.receipt_ids.length > 0 ? (
                        <Tooltip title={`View Receipt${expense.receipt_ids.length > 1 ? `s (${expense.receipt_ids.length})` : ''}`}>
                          <IconButton 
                            size="small" 
                            onClick={() => fetchReceipt(expense.revolut_id, expense.receipt_ids[0])}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Badge badgeContent={expense.receipt_ids.length} color="primary">
                              <ReceiptIcon />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No receipts
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className="fit-table-no-clip">
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetails(expense)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {expenseData?.pagination && expenseData.pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={expenseData.pagination.total_pages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </CardContent>
  </Card>

      {/* Expense Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Expense Details</DialogTitle>
        <DialogContent>
          {selectedExpense && (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Expense ID:</Typography>
                  <Typography variant="body1">{selectedExpense.revolut_id}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">State:</Typography>
                  <Chip 
                    label={selectedExpense.state.replace('_', ' ')} 
                    color={getStateColor(selectedExpense.state) as any}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Amount:</Typography>
                  <Typography variant="body1">{formatAmount(selectedExpense.spent_amount)}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Category:</Typography>
                  <Typography variant="body1">{selectedExpense.category.name || 'N/A'}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 100%' }}>
                  <Typography variant="body2" color="text.secondary">Description:</Typography>
                  <Typography variant="body1">{selectedExpense.description || 'N/A'}</Typography>
                </Box>
              </Box>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Raw Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedExpense, null, 2)}
                    </pre>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Viewer Modal */}
      <ReceiptViewerModal
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        expenseId={selectedExpenseForReceipt?.revolut_id || ''}
        receiptId={selectedReceiptId}
        expenseTitle={selectedExpenseForReceipt?.description || 'N/A'}
        expenseAmount={selectedExpenseForReceipt?.spent_amount?.value || 0}
        expenseDate={selectedExpenseForReceipt?.expense_date}
      />

      {/* Historical Sync Progress Modal */}
      <Dialog
        open={progressModalOpen}
        onClose={() => {
          // Allow closing, but warn if sync is still running
          if (syncProgress?.status === 'running') {
            if (window.confirm('Sync is still in progress. Close the modal? (Sync will continue in the background)')) {
              setProgressModalOpen(false);
              // Keep polling in background but don't show modal
            }
          } else {
            setProgressModalOpen(false);
          }
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle>
          {syncType === 'historical' ? 'Historical Expenses Sync Progress' : 'Expenses Sync Progress'}
          {syncType && (
            <Chip 
              label={syncType === 'historical' ? 'Full Historical' : 'Incremental'} 
              size="small" 
              color={syncType === 'historical' ? 'warning' : 'success'}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {!syncProgress ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Starting historical expenses sync...
              </Typography>
            </Box>
          ) : syncProgress.status === 'not_running' ? (
            <Alert severity="info">
              No expenses sync in progress
            </Alert>
          ) : (
            <Box>
              {/* Overall Progress */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Overall Progress
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {Math.round(syncProgress.progress_percentage || 0)}%
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${syncProgress.progress_percentage || 0}%`,
                      height: '100%',
                      bgcolor: syncProgress.status === 'failed' ? 'error.main' : 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {syncProgress.status === 'completed' ? (
                      <>All {syncProgress.total_weeks || 0} weeks completed</>
                    ) : syncProgress.status === 'failed' ? (
                      <>Sync failed</>
                    ) : syncProgress.current_week ? (
                      <>Week {syncProgress.current_week.week_number} of {syncProgress.total_weeks || 0}</>
                    ) : (
                      <>Week {syncProgress.completed_weeks?.length || 0} of {syncProgress.total_weeks || 0}</>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {syncProgress.total_expenses || 0} expenses synced
                  </Typography>
                </Box>
                {syncProgress.elapsed_seconds && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Elapsed time: {Math.round(syncProgress.elapsed_seconds / 60)} minutes
                  </Typography>
                )}
              </Box>

              {/* Current Week - only show when sync is running and we have a current week */}
              {syncProgress.current_week && syncProgress.status === 'running' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Current Week:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      {new Date(syncProgress.current_week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(syncProgress.current_week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                    <Chip
                      label="Processing..."
                      size="small"
                      color="primary"
                    />
                  </Box>
                </Box>
              )}

              {/* Recent Progress */}
              {syncProgress.completed_weeks && syncProgress.completed_weeks.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Recent Progress:
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {syncProgress.completed_weeks.slice(-10).reverse().map((week: any, index: number) => (
                      <Box
                        key={`completed-${week.week_number}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          px: 2,
                          mb: 0.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2">
                            Week {week.week_number}: {new Date(week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              label={`${week.expense_count || 0} expenses`}
                              size="small"
                              color={week.expense_count > 0 ? 'success' : 'default'}
                            />
                            {week.receipt_count !== undefined && (
                              <Chip
                                label={`${week.receipt_count || 0} receipts`}
                                size="small"
                                color={week.receipt_count > 0 ? 'info' : 'default'}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Success Message */}
              {syncProgress.status === 'completed' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Historical expenses sync completed successfully!
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Total expenses synced: {syncProgress.total_expenses || 0}
                  </Typography>
                </Alert>
              )}

              {/* Error Message */}
              {syncProgress.status === 'failed' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    {(() => {
                      let errorMsg = syncProgress.error || 'Historical expenses sync failed';
                      // Truncate if too long
                      if (errorMsg.length > 200) {
                        errorMsg = errorMsg.substring(0, 200) + '...';
                      }
                      // Remove HTML tags if present
                      errorMsg = errorMsg.replace(/<[^>]*>/g, '');
                      return errorMsg;
                    })()}
                  </Typography>
                  {syncProgress.failed_weeks && syncProgress.failed_weeks.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Failed Weeks:
                      </Typography>
                      {syncProgress.failed_weeks.slice(0, 5).map((week: any, index: number) => {
                        // Truncate error message if it's too long
                        let errorMsg = week.error || 'Unknown error';
                        if (errorMsg.length > 200) {
                          errorMsg = errorMsg.substring(0, 200) + '...';
                        }
                        // Remove HTML tags if present
                        errorMsg = errorMsg.replace(/<[^>]*>/g, '');
                        
                        return (
                          <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                            • Week {week.week_number} ({new Date(week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}): {errorMsg}
                          </Typography>
                        );
                      })}
                      {syncProgress.failed_weeks.length > 5 && (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1, fontStyle: 'italic' }}>
                          ... and {syncProgress.failed_weeks.length - 5} more failed weeks
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(() => {
            const shouldShowStopButton = syncProgress?.status === 'running' || isSyncing;
            return shouldShowStopButton ? (
              <Button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to stop the sync? Progress will be saved and you can resume later.')) {
                    await stopSync();
                  }
                }}
                color="error"
                variant="contained"
                sx={{ mr: 'auto' }}
              >
                Stop Sync
              </Button>
            ) : (
              <Box sx={{ flex: 1 }} /> // Spacer when button is not shown
            );
          })()}
          <Button
            onClick={() => {
              if (syncProgress?.status === 'running') {
                if (window.confirm('Sync is still in progress. Close the modal? (Sync will continue in the background)')) {
                  setProgressModalOpen(false);
                }
              } else {
                setProgressModalOpen(false);
              }
            }}
          >
            {syncProgress?.status === 'completed' ? 'Close' : syncProgress?.status === 'failed' ? 'Close' : 'Close (Sync continues in background)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Expenses Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete All Expenses</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            ⚠️ WARNING: This action cannot be undone!
          </Alert>
          <Typography>
            This will permanently delete <strong>ALL expenses</strong> from the database.
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Current expenses in database: <strong>{expenseData?.pagination?.total_count || 0}</strong>
          </Typography>
          <Typography sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Receipt files on disk and receipt IDs in the database will be deleted.
            </Typography>
          </Typography>
          <Typography sx={{ mt: 2, color: 'error.main' }}>
            Are you sure you want to proceed? This is useful if you want to start a fresh historical sync.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllExpenses}
            disabled={deleting}
            color="error"
            variant="contained"
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete All Expenses'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RevolutExpenses;
